import React, {useEffect, useState} from 'react';
import Headline from 'components/Headline/styles';
import {DashboardContent} from 'components/DashboardContent/styles';
import {DashboardHeader} from 'components/DashboardHeader/styles';
import IconHelpCenter from 'assets/help/IconHelpCenter';
import IconHelpChat from 'assets/help/IconHelpChat';
import IconHelpWhatsApp from 'assets/help/IconHelpWhatsApp';
import IconExternalLink from 'assets/help/IconExternalLink';
import SuccessManagerHelpCard from 'components/SuccessManager/SuccessManagerHelpCard';
import {
  HelpCard,
  HelpCardButton,
  HelpCardIcon,
  HelpCards,
  HelpCardText,
  HelpCardTitle,
  HelpSubtitle
} from 'routes/App/Help/styles';

const HELP_CENTER_URL = 'https://support.getsauce.com/';
const WHATSAPP_URL =
  'https://wa.me/18884771537?text=Hi%20Sauce%20team%2C%20I%20need%20your%20help%20with';

const openSupportChat = () => window.HubSpotConversations?.widget?.open();

// An ad blocker, or a chatflow that does not target this page, means no chat at
// all, and the card would be a dead end. The API being present only says the
// script loaded, so the card follows the widget itself: it only loads when a
// chatflow targets the page, which is the same thing that puts the bubble in
// the corner.
const useSupportChatAvailable = () => {
  const [available, setAvailable] = useState(() =>
    Boolean(window.HubSpotConversations?.widget?.status?.()?.loaded)
  );

  useEffect(() => {
    if (available) return undefined;

    let active = true;
    const markLoaded = () => {
      if (active) setAvailable(true);
    };

    const subscribe = () => {
      const hubspot = window.HubSpotConversations;
      if (!hubspot) return;
      if (hubspot.widget.status().loaded) {
        markLoaded();
        return;
      }
      hubspot.on('widgetLoaded', markLoaded);
      hubspot.widget.load();
    };

    subscribe();
    window.hsConversationsOnReady = [
      ...(window.hsConversationsOnReady || []),
      subscribe
    ];

    return () => {
      active = false;
      window.HubSpotConversations?.off?.('widgetLoaded', markLoaded);
      window.hsConversationsOnReady = (
        window.hsConversationsOnReady || []
      ).filter((callback) => callback !== subscribe);
    };
  }, [available]);

  return available;
};

const Help = () => {
  const supportChatAvailable = useSupportChatAvailable();

  return (
    <DashboardContent className='DashboardContent'>
      <DashboardHeader className='AlwaysFlex'>
        <Headline className='mTop5'>How can we help?</Headline>
      </DashboardHeader>
      <HelpSubtitle>
        Choose the support option that works best for you.
      </HelpSubtitle>

      <HelpCards>
        <SuccessManagerHelpCard />

        <HelpCard>
          <HelpCardIcon>
            <IconHelpCenter />
          </HelpCardIcon>
          <HelpCardTitle>Help Center</HelpCardTitle>
          <HelpCardText>Find answers and step-by-step guides.</HelpCardText>
          <HelpCardButton
            as='a'
            className='ButtonMedium solid'
            href={HELP_CENTER_URL}
            target='_blank'
            rel='noopener noreferrer'
          >
            <span>
              Go to Help Center
              <IconExternalLink />
            </span>
          </HelpCardButton>
        </HelpCard>

        {supportChatAvailable && (
          <HelpCard>
            <HelpCardIcon>
              <IconHelpChat />
            </HelpCardIcon>
            <HelpCardTitle>Chat with support</HelpCardTitle>
            <HelpCardText>
              Chat with our support team in the dashboard.
            </HelpCardText>
            <HelpCardButton
              type='button'
              className='ButtonMedium solid'
              onClick={openSupportChat}
            >
              <span>Start chat</span>
            </HelpCardButton>
          </HelpCard>
        )}

        <HelpCard>
          <HelpCardIcon className='green'>
            <IconHelpWhatsApp />
          </HelpCardIcon>
          <HelpCardTitle>WhatsApp support</HelpCardTitle>
          <HelpCardText>Message our support team on WhatsApp.</HelpCardText>
          <HelpCardButton
            as='a'
            className='ButtonMedium solid'
            href={WHATSAPP_URL}
            target='_blank'
            rel='noopener noreferrer'
          >
            <span>
              Open WhatsApp
              <IconExternalLink />
            </span>
          </HelpCardButton>
        </HelpCard>
      </HelpCards>
    </DashboardContent>
  );
};

export default Help;
